import React, {useEffect, useState, useMemo} from 'react';
import {observer} from 'mobx-react-lite';
import {reaction} from 'mobx';
import {RendleyStore} from '../../../../store/RendleyStore';
import {TimelineStore} from "../../../../store/TimelineStore";
import {RendleyService} from "../../../../services/RendleyService";
import {ApplicationStore} from "../../../../store/ApplicationStore";
import LibrettoClip from "../LibrettoClip/LibrettoClip";
import WaveformDisplay from "../WaveformDisplay/WaveformDisplay";
import {ParseTranscriptWords} from "../../../../../pages/Editor/Transcript";
import '../AudioClipWithWaveform/AudioClipWithWaveform.styles.scss';
import {MEDIA_WAVEFORM_DATA, MEDIA_TRANSCRIPT} from "../../../../Constants";

interface LibrettoAudioClipWithWaveformProps {
    librettoClipId: string;
    layerId: string;
}

export const LibrettoAudioClipWithWaveform: React.FC<LibrettoAudioClipWithWaveformProps> = observer(({ librettoClipId, layerId }) => {
    const [filename, setFilename] = useState<string | undefined>();
    const [thumbnail, setThumbnail] = useState<string | undefined>();
    const [waveformData, setWaveformData] = useState<Float32Array | null>(null);
    const [duration, setDuration] = useState<number>(0);
    const [clipIds, setClipIds] = useState<string[]>([]);

    useEffect(() => {
        const disposer = reaction(
            () => {
                const librettoClipState = RendleyStore.getLibrettoClipState(librettoClipId);
                return {
                    librettoClipState,
                    zoom: TimelineStore.zoom,
                };
            },
            (payload) => {
                const { librettoClipState } = payload;
                
                if (!librettoClipState.combinedProperties) {
                    setClipIds([]);
                    setWaveformData(null);
                    setDuration(0);
                    setFilename(undefined);
                    setThumbnail(undefined);
                    return;
                }

                const { clipIds, combinedProperties } = librettoClipState;
                const firstClipId = combinedProperties.firstClipId;
                
                // Update basic properties
                setClipIds(clipIds);
                setDuration(combinedProperties.trimmedDuration);
                setFilename(RendleyStore.getFilenameByClipId(firstClipId));
                setThumbnail(RendleyStore.getThumbnailByClipId(firstClipId));

                // Calculate waveform data
                const arrays: Float32Array[] = [];
                clipIds.forEach(clipId => {
                    const clip = RendleyStore.clips[clipId];
                    if (!clip) return;
                    const stored = RendleyService.getMediaById(clip.mediaDataId).getCustomData(MEDIA_WAVEFORM_DATA) as Float32Array;
                    if (stored) {
                        const startIndex = Math.floor((clip.leftTrim / clip.duration) * stored.length);
                        const endIndex = Math.floor(((clip.duration - clip.rightTrim) / clip.duration) * stored.length);
                        arrays.push(stored.slice(startIndex, endIndex));
                    }
                });
                
                if (arrays.length) {
                    const length = arrays.reduce((sum, a) => sum + a.length, 0);
                    const combined = new Float32Array(length);
                    let offset = 0;
                    arrays.forEach(a => { combined.set(a, offset); offset += a.length; });
                    setWaveformData(combined);
                } else {
                    setWaveformData(null);
                }
            },
            { fireImmediately: true }
        );
        
        return () => { disposer(); };
    }, [librettoClipId]);

    // Calculate rendered words for script layer (copied from LibrettoVideoClipWithWaveform)
    const renderedWords = useMemo(() => {
        if (!clipIds.length) return [];
        
        const firstClipId = clipIds[0];
        const clip = RendleyStore.clips[firstClipId];
        const mediaId = clip?.mediaDataId;
        const isScriptLayer = RendleyStore.scriptLayerId === layerId;
        const transcriptJson = mediaId && isScriptLayer ? RendleyService.getMediaById(mediaId)?.getCustomData(MEDIA_TRANSCRIPT) : null;
        
        if (!transcriptJson || !isScriptLayer || !librettoClipId) return [];

        // 1. Collect all media segments from all clips
        const mediaSegments = clipIds.map(clipId => {
            const clip = RendleyStore.clips[clipId];
            if (!clip) return null;
            return {
                clipId,
                mediaStart: clip.leftTrim,
                mediaEnd: clip.duration - clip.rightTrim,
                clipDuration: clip.trimmedDuration
            };
        }).filter(Boolean);

        // 2. Filter words that fall within ANY clip's media segment
        const allWords = ParseTranscriptWords(transcriptJson).filter(word =>
            mediaSegments.some(segment =>
                word.start >= segment.mediaStart && word.end <= segment.mediaEnd
            )
        );

        // 3. Map words to LibrettoClip timeline positions
        const positionedWords = allWords.map(word => {
            // Find which clip this word belongs to
            const segment = mediaSegments.find(s =>
                word.start >= s.mediaStart && word.end <= s.mediaEnd
            );

            if (!segment) return null;

            // Calculate cumulative start time of this clip within LibrettoClip
            const clipIndex = clipIds.indexOf(segment.clipId);
            const cumulativeStart = mediaSegments.slice(0, clipIndex)
                .reduce((sum, s) => sum + s.clipDuration, 0);

            return {
                start: cumulativeStart + (word.start - segment.mediaStart),
                end: cumulativeStart + (word.end - segment.mediaStart),
                word: word.word
            };
        }).filter(Boolean);

        // 4. Handle row positioning for overlapping words
        const rows: { start: number; end: number; word: string; row: number }[] = [];
        positionedWords.forEach(word => {
            const wordStart = (word.start / duration) * 100;
            const wordEnd = (word.end / duration) * 100;
            let row = 0;
            while (rows.some(w => w.row === row && wordStart < (w.end / duration) * 100 && wordEnd > (w.start / duration) * 100)) {
                row++;
            }
            rows.push({ ...word, row });
        });

        return rows;
    }, [clipIds, layerId, duration, librettoClipId]);

    const WordSpan = ({word, key}) => (
        // @ts-ignore
        <span
            key={key}
            style={{
                left: `${(word.start / duration) * 100}%`,
                top: `${word.row * 1}px`,
                zIndex: 10 + word.row,
            }}
            title={word.word}
        >
            {word.word}
        </span>
    );

    const isScriptClip = RendleyStore.scriptLayerId === layerId;

    return (
        <LibrettoClip librettoClipId={librettoClipId} layerId={layerId} filename={filename} thumbnail={null} backgroundColor={"227, 227, 227"}>
            {isScriptClip && (
                <div className="clip__transcript">
                    {renderedWords.map((w, i) => (
                        <WordSpan key={i} word={w} />
                    ))}
                </div>
            )}
            {waveformData && (
                <div className="clip__waveform" style={{pointerEvents: ApplicationStore.rangeToolEnabled ? 'auto' : 'none'}}>
                    <WaveformDisplay peaks={waveformData} duration={duration} rangeEnabled={ApplicationStore.rangeToolEnabled}/>
                </div>
            )}
        </LibrettoClip>
    );
});

export default LibrettoAudioClipWithWaveform;
